import type { StreamingType } from "@spwn/types";
import type { StreamingToken } from "@spwn/types/functions";
import type * as THEOplayer from "theoplayer";

/**
 * Chromecast のレシーバに渡す配信エンドポイントを組み立てる。
 *
 * レシーバ端末は自前のプレイヤーで CloudFront を直接叩くため、ブラウザに撒いた
 * 署名付き Cookie が効かない。パスに CWT を埋めた URL を渡して認可する。
 *
 * live の HLS だけが対象。VOD は署名付き Cookie のままでも cast できる。
 * `tokenHLS` は default のエンドポイントから作られるので、ブラウザが LL-HLS を
 * 再生しているときもレシーバは通常の HLS を再生する。低遅延 (IVS) の再生中は
 * THEOplayer ごと別プレイヤーに切り替わるので cast の対象にならない。
 *
 * `isUseTokenForCast` が必要なのは、TOKEN_HLS が返ってくることが
 * 「その配信の CloudFront に /token/* behavior がある」ことを意味しないため。
 * トークンの発行はデプロイ環境単位で、behavior の追加は配信単位で行う。
 *
 * センダー側の source は流用しない。`useManagedMediaSource` や `lowLatency` は
 * ブラウザ側の再生方式の指定で、別端末のレシーバには意味がないため。
 */
export const selectCastSource = ({
  tokenHLS,
  streamingType,
  isUseTokenForCast,
  dvrStartTime,
}: {
  tokenHLS: StreamingToken | undefined;
  streamingType: StreamingType;
  isUseTokenForCast: boolean;
  dvrStartTime: number | undefined;
}): THEOplayer.SourceDescription | undefined => {
  if (!isUseTokenForCast) {
    return undefined;
  }
  if (streamingType !== "Live") {
    return undefined;
  }
  if (!tokenHLS) {
    return undefined;
  }
  return {
    sources: [
      {
        src:
          dvrStartTime === undefined
            ? tokenHLS.url
            : withStartQuery(tokenHLS.url, dvrStartTime),
        type: "application/x-mpegurl",
        liveOffset: tokenHLS.liveOffset ?? 10,
        crossOrigin: tokenHLS.cors ? "use-credentials" : "",
      },
    ],
    // これが無いとレシーバが live / DVR の UI を出せない
    streamType: dvrStartTime === undefined ? "live" : "dvr",
  };
};

/**
 * MediaPackage の time-shifted 再生に使う start パラメータを付ける。
 * @see https://docs.aws.amazon.com/ja_jp/mediapackage/latest/ug/time-shifted.html
 */
const withStartQuery = (url: string, dvrStartTime: number): string => {
  const parsed = new URL(url);
  parsed.searchParams.set("start", String(dvrStartTime));
  return parsed.toString();
};
